import type { IpgAllowedWebsite } from '../../../backend_config.types';
import type { LosslessNumber } from '../../../lossless-json-codec';

export const CloverHeartbeatOutcome = {
    SUCCESS: 'success',
    UNAUTHORIZED: 'unauthorized',
    MALFORMED_RESPONSE: 'malformed_response',
} as const;

export type CloverHeartbeatOutcome =
    (typeof CloverHeartbeatOutcome)[keyof typeof CloverHeartbeatOutcome];

export interface CloverHeartbeatRequestDetails {
    readonly method: 'GET';
    readonly path: '/v3/merchants/{merchantId}';
    readonly merchantId: string;
}

export interface CloverHeartbeatMerchantDetails {
    readonly id: string;
    readonly name?: string;
}

export interface CloverHeartbeatNormalizedResult {
    readonly merchantId: string;
    readonly status: CloverHeartbeatOutcome;
    readonly message?: string;
    readonly merchantName?: string;
}

export type CloverHeartbeatResponseDetails =
    | {
          readonly outcome: typeof CloverHeartbeatOutcome.SUCCESS;
          readonly httpStatus: 200;
          readonly body: CloverHeartbeatMerchantDetails;
      }
    | {
          readonly outcome: typeof CloverHeartbeatOutcome.UNAUTHORIZED;
          readonly httpStatus: 401 | 403;
          readonly body: unknown;
          readonly message: string;
      }
    | {
          readonly outcome: typeof CloverHeartbeatOutcome.MALFORMED_RESPONSE;
          readonly httpStatus: number;
          readonly body: unknown;
          readonly message: string;
      };

export const CloverReadinessCheckStatus = {
    READY: 'READY',
    NOT_READY: 'NOT_READY',
    UNVERIFIED: 'UNVERIFIED',
} as const;

export type CloverReadinessCheckStatus =
    (typeof CloverReadinessCheckStatus)[keyof typeof CloverReadinessCheckStatus];

export const CloverReadinessFailureReason = {
    UNAUTHORIZED: 'UNAUTHORIZED',
    UNAVAILABLE: 'UNAVAILABLE',
    OFFLINE: 'OFFLINE',
    MALFORMED_RESPONSE: 'MALFORMED_RESPONSE',
    TIMEOUT: 'TIMEOUT',
    INACTIVE: 'INACTIVE',
    NOT_CONFIGURED: 'NOT_CONFIGURED',
    UNSUPPORTED: 'UNSUPPORTED',
} as const;

export type CloverReadinessFailureReason =
    (typeof CloverReadinessFailureReason)[keyof typeof CloverReadinessFailureReason];

export const CloverReadinessUnverifiedReason = {
    SAFE_PROBE_UNAVAILABLE: 'SAFE_PROBE_UNAVAILABLE',
} as const;

export type CloverReadinessUnverifiedReason =
    (typeof CloverReadinessUnverifiedReason)[keyof typeof CloverReadinessUnverifiedReason];

export interface CloverReadinessReadyCheck {
    readonly status: typeof CloverReadinessCheckStatus.READY;
}

export interface CloverReadinessNotReadyCheck {
    readonly status: typeof CloverReadinessCheckStatus.NOT_READY;
    readonly reason: CloverReadinessFailureReason;
    readonly message: string;
}

export interface CloverReadinessUnverifiedCheck {
    readonly status: typeof CloverReadinessCheckStatus.UNVERIFIED;
    readonly reason: CloverReadinessUnverifiedReason;
    readonly message: string;
}

export type CloverReadinessCheck =
    | CloverReadinessReadyCheck
    | CloverReadinessNotReadyCheck
    | CloverReadinessUnverifiedCheck;

// Discriminator: supported
export type CloverSurchargeConfiguration =
    | {
          readonly supported: false;
      }
    | {
          readonly supported: true;
          readonly rate: LosslessNumber;
      };

export interface CloverSurchargeReadiness {
    readonly check: CloverReadinessCheck;
    readonly configuration: CloverSurchargeConfiguration | null;
}

/**
 * One terminal's readiness, holding only the checks its type uses: a virtual
 * terminal reports the card-not-present checks and no Cloud Pay device
 * (BUG-256); a physical terminal reports credentials and its Cloud Pay device.
 */
export interface CloverReadinessResult {
    readonly credential: CloverReadinessCheck;
    /** Card-not-present only: absent for a physical terminal. */
    readonly ecommerceKey?: CloverReadinessCheck;
    /** Absent for a virtual terminal: Cloud Pay Display needs a physical device (BUG-256). */
    readonly cloudPayDevice?: CloverReadinessCheck;
    /** Card-not-present only: absent for a physical terminal. */
    readonly multiPay?: CloverReadinessCheck;
    /** Card-not-present only: absent for a physical terminal. */
    readonly surcharge?: CloverSurchargeReadiness;
}

/**
 * Where the OAuth callback sends the browser after Clover's consent. The Portal
 * route tells the tab that started the connection, then closes itself; the
 * query carries only these result and reason codes (BUG-278).
 */
export const CLOVER_OAUTH_COMPLETION_PATH = '/integrations/clover/oauth-complete';

/** The BroadcastChannel the completion route uses to reach the Portal tabs. */
export const CLOVER_OAUTH_COMPLETION_CHANNEL = 'clover-oauth';

export const CloverOAuthCompletionResult = {
    CONNECTED: 'connected',
    ERROR: 'error',
} as const;

export type CloverOAuthCompletionResult =
    (typeof CloverOAuthCompletionResult)[keyof typeof CloverOAuthCompletionResult];

export const CloverOAuthCompletionReason = {
    /** Clover returned without a usable authorization, e.g. consent was declined. */
    INCOMPLETE: 'incomplete',
    /** The connection request expired or was already used. */
    EXPIRED: 'expired',
    /** Clover connected a different merchant than the one that started it. */
    MERCHANT_MISMATCH: 'merchant_mismatch',
    /** The callback came from another Clover app registration. */
    CLIENT_MISMATCH: 'client_mismatch',
    /** Clover did not accept the connection. */
    PROVIDER_REJECTED: 'provider_rejected',
    /** Clover or Anchorbase could not complete it right now. */
    UNAVAILABLE: 'unavailable',
} as const;

export type CloverOAuthCompletionReason =
    (typeof CloverOAuthCompletionReason)[keyof typeof CloverOAuthCompletionReason];

// Discriminator: result
export type CloverOAuthCompletion =
    | { readonly result: typeof CloverOAuthCompletionResult.CONNECTED }
    | {
          readonly result: typeof CloverOAuthCompletionResult.ERROR;
          readonly reason: CloverOAuthCompletionReason;
      };

export const CloverOAuthAuthorizationState = {
    NOT_CONNECTED: 'NOT_CONNECTED',
    ACTIVE: 'ACTIVE',
    REAUTHORIZATION_REQUIRED: 'REAUTHORIZATION_REQUIRED',
    DISCONNECTED: 'DISCONNECTED',
} as const;

export type CloverOAuthAuthorizationState =
    (typeof CloverOAuthAuthorizationState)[keyof typeof CloverOAuthAuthorizationState];

export const CloverOAuthRefreshIntervalMinutes = {
    MINIMUM: 1,
    MAXIMUM: 43_200,
    DEFAULT: 43_200,
} as const;

export const CloverOAuthRefreshScheduleState = {
    SCHEDULED: 'SCHEDULED',
    NOT_CONNECTED: 'NOT_CONNECTED',
    INACTIVE: 'INACTIVE',
    REAUTHORIZATION_REQUIRED: 'REAUTHORIZATION_REQUIRED',
    SCHEDULING_PENDING: 'SCHEDULING_PENDING',
} as const;

export type CloverOAuthRefreshScheduleState =
    (typeof CloverOAuthRefreshScheduleState)[keyof typeof CloverOAuthRefreshScheduleState];

export type CloverOAuthRefreshSchedule =
    | {
          readonly state: typeof CloverOAuthRefreshScheduleState.SCHEDULED;
          readonly next_refresh_executes_ts: string;
          readonly merchant_timezone: import('../../../timezone.utils').MerchantTimeZone | null;
      }
    | {
          readonly state: Exclude<
              CloverOAuthRefreshScheduleState,
              typeof CloverOAuthRefreshScheduleState.SCHEDULED
          >;
      };

export interface CloverOAuthAuthorizationStatus {
    readonly state: CloverOAuthAuthorizationState;
    readonly access_token_expires_ts: string | null;
    readonly refresh_token_expires_ts: string | null;
    readonly merchant_timezone: import('../../../timezone.utils').MerchantTimeZone | null;
    readonly refresh_interval_minutes: number;
    readonly refresh_schedule: CloverOAuthRefreshSchedule;
}

export interface RenewCloverOAuthAuthorizationRequest {
    readonly organization_uuid: string;
}

export interface UpdateCloverOAuthRefreshIntervalRequest {
    readonly organization_uuid: string;
    readonly refresh_interval_minutes: number;
}

// Discriminator: renewal_outcome
export type RenewCloverOAuthAuthorizationResponse =
    | {
          readonly renewal_outcome: 'REFRESHED';
      }
    | {
          readonly renewal_outcome: 'REAUTHORIZATION_REQUIRED';
          readonly authorization_url: string;
          readonly expires_ts: string;
          readonly allowed_websites: readonly IpgAllowedWebsite[];
      };

export const CloverPaymentsInfrastructureState = {
    NOT_CONNECTED: 'NOT_CONNECTED',
    NOT_PROVISIONED: 'NOT_PROVISIONED',
    PROVISIONED: 'PROVISIONED',
    /** Two or more pre-existing Clover Merchant Accounts; provisioning waits for help (FEAT-317). */
    MERCHANT_ACCOUNT_CONFLICT: 'MERCHANT_ACCOUNT_CONFLICT',
} as const;

export type CloverPaymentsInfrastructureState =
    (typeof CloverPaymentsInfrastructureState)[keyof typeof CloverPaymentsInfrastructureState];

export interface CloverPaymentsInfrastructureRequest {
    readonly organization_uuid: string;
}

export const CloverPaymentsInfrastructureTerminalKind = {
    VIRTUAL: 'VIRTUAL',
    PHYSICAL: 'PHYSICAL',
} as const;

/** One active Clover terminal reconciled from the last Clover fleet read. */
// Discriminator: kind
export type CloverPaymentsInfrastructureTerminal =
    | {
          readonly kind: typeof CloverPaymentsInfrastructureTerminalKind.VIRTUAL;
          readonly terminal_device_uuid: string;
          readonly name: string | null;
      }
    | {
          readonly kind: typeof CloverPaymentsInfrastructureTerminalKind.PHYSICAL;
          readonly terminal_device_uuid: string;
          readonly name: string | null;
          readonly serial: string;
      };

export interface CloverPaymentsInfrastructureStatus {
    readonly state: CloverPaymentsInfrastructureState;
    readonly clover_merchant_id: string | null;
    readonly clover_merchant_name: string | null;
    readonly organization_name: string;
    readonly merchant_name_mismatch: boolean;
    /** Whether the Organization has an active PRINCIPAL, who can take Clover payments (BUG-270). */
    readonly principal_assigned: boolean;
    readonly virtual_terminal_count: number;
    readonly physical_terminal_count: number;
    /** Empty until provisioned; the virtual terminal first, then devices in creation order. */
    readonly terminals: readonly CloverPaymentsInfrastructureTerminal[];
    /** The pre-existing Clover Merchant Accounts in a MERCHANT_ACCOUNT_CONFLICT, otherwise empty. */
    readonly conflicting_merchant_accounts: readonly CloverConflictingMerchantAccount[];
}

/** A pre-existing Clover Merchant Account that provisioning could not choose between (FEAT-317). */
export interface CloverConflictingMerchantAccount {
    readonly merchant_account_uuid: string;
    readonly name: string | null;
    readonly processor_mid: string;
}

export interface CloverPaymentsInfrastructureSynchronizationResult {
    readonly status: CloverPaymentsInfrastructureStatus;
    readonly created_virtual_terminal_count: number;
    readonly created_physical_terminal_count: number;
    readonly restored_virtual_terminal_count: number;
    readonly restored_physical_terminal_count: number;
    readonly removed_virtual_terminal_count: number;
    readonly removed_physical_terminal_count: number;
    /** Serials Clover reported that could not be set up; the others were (BUG-270 D3). */
    readonly failed_physical_device_serials: readonly string[];
}
